import React, { useState, useEffect } from "react";
import "./UsersPage.css";
import { apiGet, apiPost, apiPut, apiDelete } from "../../services/api";
import { User, Team, UserRole } from "../../types";
import { SidePanel } from "../shared/SidePanel";
import { FormField, Button } from "../shared/FormField";

const ROLES: UserRole[] = ["sales_rep", "sales_manager", "admin"];

export function UsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"users" | "teams">("users");

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", role: "sales_rep" as UserRole });
  const [teamPanelOpen, setTeamPanelOpen] = useState(false);
  const [teamForm, setTeamForm] = useState({ name: "", managerId: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [usersResult, teamsResult] = await Promise.all([
        apiGet<{ items: User[] }>("/api/users"),
        apiGet<{ items: Team[] }>("/api/teams"),
      ]);
      setUsers(usersResult.items);
      setTeams(teamsResult.items);
    } catch (err) {
      console.error("Failed to load data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    setSaving(true);
    try {
      await apiPost("/api/users/invite", inviteForm);
      setInviteOpen(false);
      await loadData();
    } catch (err) {
      console.error("Failed to invite user:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTeam = async () => {
    setSaving(true);
    try {
      await apiPost("/api/teams", teamForm);
      setTeamPanelOpen(false);
      await loadData();
    } catch (err) {
      console.error("Failed to create team:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (userId: string, role: UserRole) => {
    try {
      await apiPut(`/api/users/${userId}/role`, { role });
      await loadData();
    } catch (err) {
      console.error("Failed to update role:", err);
    }
  };

  const handleTeamAssign = async (userId: string, teamId: string) => {
    try {
      if (teamId) {
        await apiPut(`/api/users/${userId}/team`, { teamId });
      } else {
        await apiDelete(`/api/users/${userId}/team`);
      }
      await loadData();
    } catch (err) {
      console.error("Failed to assign team:", err);
    }
  };

  const managers = users.filter((u) => u.role === "sales_manager");

  if (loading) return <div className="protrain-admin-loading">Loading users...</div>;

  return (
    <div className="protrain-users-page">
      <div className="protrain-admin-header">
        <h2 className="protrain-page-title">Users & Teams</h2>
        <div className="protrain-users-actions">
          <Button variant="secondary" onClick={() => { setTeamForm({ name: "", managerId: "" }); setTeamPanelOpen(true); }}>+ New Team</Button>
          <Button onClick={() => { setInviteForm({ email: "", name: "", role: "sales_rep" }); setInviteOpen(true); }}>+ Invite User</Button>
        </div>
      </div>

      <div className="protrain-users-tabs">
        <button className={`protrain-users-tab ${tab === "users" ? "protrain-users-tab--active" : ""}`} onClick={() => setTab("users")}>
          Users ({users.length})
        </button>
        <button className={`protrain-users-tab ${tab === "teams" ? "protrain-users-tab--active" : ""}`} onClick={() => setTab("teams")}>
          Teams ({teams.length})
        </button>
      </div>

      {tab === "users" && (
        <table className="protrain-users-table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Role</th><th>Team</th><th>Status</th></tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.userId}>
                <td className="protrain-users-name">{user.name}</td>
                <td className="protrain-users-email">{user.email}</td>
                <td>
                  <select className="protrain-users-role-select" value={user.role} onChange={(e) => handleRoleChange(user.userId, e.target.value as UserRole)}>
                    {ROLES.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
                  </select>
                </td>
                <td>
                  <select className="protrain-users-team-select" value={user.teamId || ""} onChange={(e) => handleTeamAssign(user.userId, e.target.value)}>
                    <option value="">No team</option>
                    {teams.map((t) => <option key={t.teamId} value={t.teamId}>{t.name}</option>)}
                  </select>
                </td>
                <td><span className={`protrain-users-status protrain-users-status--${user.status}`}>{user.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {tab === "teams" && (
        <div className="protrain-teams-grid">
          {teams.map((team) => (
            <div key={team.teamId} className="protrain-teams-card">
              <h3 className="protrain-teams-card-name">{team.name}</h3>
              <div className="protrain-teams-card-meta">
                <span>Manager: {users.find((u) => u.userId === team.managerId)?.name || team.managerId}</span>
                <span>Members: {users.filter((u) => u.teamId === team.teamId).length}</span>
              </div>
            </div>
          ))}
          {teams.length === 0 && <div className="protrain-admin-empty">No teams yet.</div>}
        </div>
      )}

      <SidePanel isOpen={inviteOpen} onClose={() => setInviteOpen(false)} title="Invite User" footer={
        <><Button variant="secondary" onClick={() => setInviteOpen(false)}>Cancel</Button><Button onClick={handleInvite} disabled={saving || !inviteForm.email || !inviteForm.name}>{saving ? "Inviting..." : "Send Invite"}</Button></>
      }>
        <FormField label="Name" htmlFor="invite-name"><input id="invite-name" value={inviteForm.name} onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })} /></FormField>
        <FormField label="Email" htmlFor="invite-email"><input id="invite-email" type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} /></FormField>
        <FormField label="Role" htmlFor="invite-role">
          <select id="invite-role" value={inviteForm.role} onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value as UserRole })}>
            {ROLES.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
          </select>
        </FormField>
      </SidePanel>

      <SidePanel isOpen={teamPanelOpen} onClose={() => setTeamPanelOpen(false)} title="New Team" footer={
        <><Button variant="secondary" onClick={() => setTeamPanelOpen(false)}>Cancel</Button><Button onClick={handleCreateTeam} disabled={saving || !teamForm.name}>{saving ? "Creating..." : "Create Team"}</Button></>
      }>
        <FormField label="Team Name" htmlFor="team-name"><input id="team-name" value={teamForm.name} onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })} /></FormField>
        <FormField label="Manager" htmlFor="team-manager">
          <select id="team-manager" value={teamForm.managerId} onChange={(e) => setTeamForm({ ...teamForm, managerId: e.target.value })}>
            <option value="">Select a manager...</option>
            {managers.map((m) => <option key={m.userId} value={m.userId}>{m.name}</option>)}
          </select>
        </FormField>
      </SidePanel>
    </div>
  );
}
